import * as yup from 'yup';

export const actionSchema = yup.object().shape({
  actionName: yup
    .string()
    .required('This field is required')
    .matches(/^[a-zA-Z0-9Ա-Ֆա-ֆ]+(?:[ .a-zA-Z0-9Ա-Ֆա-ֆ:#\-/. '&() ]*)$/, {
      message: 'Invalid characters',
      excludeEmptyString: true,
    })
    .max(50, 'Max length: 50 characters'),
  description: yup.string().max(250, 'Max length exceeded').nullable(),
  effectiveDate: yup
    .date()
    .typeError('Invalid date format')
    .required('This field is required')
    .test('is-future', 'Date cannot be in the past', (value) => {
      if (!value) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return value >= today;
    }),
});
export const timeSettingsSchema = yup.object().shape({
  submissionTime: yup
    .number()
    .transform((val, orig) => {
      if (orig === '') return undefined;
      const normalized = Number(orig);
      return isNaN(normalized) ? val : normalized;
    })
    .typeError('Enter a positive number')
    .integer('Enter a whole number only')
    .positive('Enter a positive number')
    .required('This field is required')
    .test('submission-less-than-reminder', function (value) {
      const { reminderValue } = this.parent;

      if (value === undefined || reminderValue === undefined) return true;

      if (value < reminderValue) {
        return this.createError({
          message: 'Submission Time must be greater than to Reminder Time',
        });
      }

      return true;
    }),

  reviewTime: yup
    .number()
    .transform((val, orig) => {
      if (orig === '') return undefined;
      const normalized = Number(orig);
      return isNaN(normalized) ? val : normalized;
    })
    .typeError('Enter a positive number')
    .integer('Enter a whole number only')
    .positive('Enter a positive number')
    .required('This field is required')
    .test(
      'greater-than-reminder',
      'Manager Review Time must be greater than Reminder',
      function (value) {
        const { reminderValue } = this.parent;
        if (!reminderValue) return true;
        return value > reminderValue;
      }
    ),

  reminderValue: yup
    .number()
    .transform((val, orig) => {
      if (orig === '') return undefined;
      const normalized = Number(orig);
      return isNaN(normalized) ? val : normalized;
    })
    .typeError('Enter a positive number')
    .integer('Enter a whole number only')
    .positive('Enter a positive number')
    .required('This field is required')
    .test(
      'reminder-validation',
      'Reminder must be less than Submission and Review Time',
      function (value) {
        const { submissionTime, reviewTime } = this.parent;

        const isLessSubmission = !submissionTime || value < submissionTime;
        const isLessReview = !reviewTime || value < reviewTime;

        return isLessSubmission && isLessReview;
      }
    ),
});
export const assignmentSchema = yup.object().shape({
  action: yup.object().nullable().required('Action is required'),
  effectiveDate: yup
    .date()
    .typeError('Effective date is required')
    .required('Effective date is required'),
  actionType: yup.object().nullable().required('Action type is required'),
  estimationType: yup.object().nullable().required('Estimation type is required'),

  selectedEmployees: yup.array().when('$assignmentType', {
    is: 'employee',
    then: (schema) => schema.min(1, 'At least one employee must be selected').required(),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  selectedDepartment: yup.array().when('$assignmentType', {
    is: 'department',
    then: (schema) => schema.min(1, 'At least one department must be selected').required(),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  kpiConfig: yup.object().shape({
    duration: yup.object().nullable().required('Duration is required'),

    qLowAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .test('max-digits', 'Value must not exceed 10 digits', (val) => {
        if (val === undefined || val === null) return true;
        return val.toString().length <= 10;
      })
      .when(['$estType', 'qTargetAmount'], {
        is: (estType) => estType === 'quantity' || estType === 'both',
        then: (schema) =>
          schema
            .required('This field is required')
            .test('low-less-than-target', 'Low must be less than Target', function (value) {
              const { qTargetAmount } = this.parent;

              if (value == null || qTargetAmount == null) return true;

              return value < qTargetAmount;
            }),
      }),

    qTargetAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .test('target-range', function (value) {
        const { qLowAmount, qHighAmount } = this.parent;

        if (value == null) return true;

        if (qLowAmount != null && value <= qLowAmount) {
          return this.createError({
            message: 'Target must be greater than Low',
          });
        }

        if (qHighAmount != null && value >= qHighAmount) {
          return this.createError({
            message: 'Target must be less than High',
          });
        }

        return true;
      })
      .when('$estType', {
        is: (val) => val === 'quantity' || val === 'both',
        then: (schema) => schema.required('This field is required'),
      }),

    qHighAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .test('max-digits', 'Value must not exceed 10 digits', (val) => {
        if (val === undefined || val === null) return true;
        return val.toString().length <= 10;
      })
      .when('$estType', {
        is: (val) => val === 'quantity' || val === 'both',
        then: (schema) =>
          schema
            .required('This field is required')
            .test(
              'high-greater-than-target',
              'High must be greater than Target',
              function (value) {
                return (
                  value == null ||
                  !this.parent.qTargetAmount ||
                  value > this.parent.qTargetAmount
                );
              }
            ),
      }),

    currency: yup
      .object()
      .nullable()
      .when('$estType', {
        is: (val) => val === 'financial' || val === 'both',
        then: (schema) => schema.required('This field is required'),
      }),

    lowAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .when('$estType', {
        is: (val) => val === 'financial' || val === 'both',
        then: (schema) =>
          schema
            .required('This field is required')
            .test(
              'low-less-than-target-fin',
              'Low must be less than Target',
              function (value) {
                const { targetAmount } = this.parent;
                if (value === undefined || targetAmount === undefined) return true;
                return value < targetAmount;
              }
            ),
      }),

    targetAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .when('$estType', {
        is: (val) => val === 'financial' || val === 'both',
        then: (schema) =>
          schema
            .required('This field is required')
            .test('target-range-fin', 'Target must be between Low and High', function (value) {
              const { lowAmount, highAmount } = this.parent;

              if (value == null) return true;

              if (lowAmount != null && value <= lowAmount) {
                return this.createError({ message: 'Target must be greater than Low' });
              }

              if (highAmount != null && value >= highAmount) {
                return this.createError({ message: 'Target must be less than High' });
              }

              return true;
            }),
      }),

    highAmount: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .when('$estType', {
        is: (val) => val === 'financial' || val === 'both',
        then: (schema) =>
          schema
            .required('This field is required')
            .test(
              'high-greater-than-target-fin',
              'High must be greater than Target',
              function (value) {
                return (
                  value == null ||
                  !this.parent.targetAmount ||
                  value > this.parent.targetAmount
                );
              }
            ),
      }),
  }),

  compensationConfig: yup.object().shape({
    belowLow: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .max(100, 'Value must be ≤ 100%')
      .required('This field is required'),
    lowToTarget: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .max(100, 'Value must be ≤ 100%')
      .required('This field is required'),
    targetToHigh: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .max(100, 'Value must be ≤ 100%')
      .required('This field is required'),
    aboveHigh: yup
      .number()
      .transform((val, orig) => (orig === '' ? undefined : val))
      .typeError('Enter a valid number')
      .min(0, 'Cannot be negative')
      .max(100, 'Value must be ≤ 100%')
      .required('This field is required'),
  }),
});
